import * as THREE from 'three';
import { WHEELS } from '../core/SimConfig.js';
import { DEG2RAD, clamp, rand, smoothstep, srand } from '../utils.js';

/**
 * Configuration for each landing gear component.
 *
 *  mesh       — name of the Object3D in the GLB scene graph
 *  axis       — local rotation axis: 'x', 'y', or 'z'
 *  angleDeg   — rotation angle when fully deployed (degrees)
 *  phaseStart — gear progress (0–1) at which this component begins moving
 *  phaseEnd   — gear progress (0–1) at which this component is fully deployed
 *
 * The model stores gear in the RETRACTED pose (all rotations zero).
 * Components map a slice of the master gear value [phaseStart, phaseEnd] to
 * their full rotation, enabling sequenced animation (doors first, then struts).
 */
export const GEAR_CONFIG = [
  // ── Doors (open first, fast) ──────────────────────────────────────────────
  { mesh: '_Gear_Front_Door',      axis: 'z', angleDeg:  75, phaseStart: 0.00, phaseEnd: 0.25 },
  { mesh: '_Gear_Main_Door_Left',  axis: 'x', angleDeg:  -90, phaseStart: 0.15, phaseEnd: 0.9 },
  { mesh: '_Gear_Main_Door_Right', axis: 'x', angleDeg: -90, phaseStart: 0.10, phaseEnd: 0.85 },

  // ── Nose gear (slightly faster than mains) ────────────────────────────────
  { mesh: '_Gear_Front',           axis: 'x', angleDeg:  -85, phaseStart: 0.15, phaseEnd: 0.60 },

  // ── Main gear (slowest, start last) ───────────────────────────────────────
  { mesh: '_Gear_Main_Left',       axis: 'x', angleDeg:  -90, phaseStart: 0.15, phaseEnd: 0.9 },
  { mesh: '_Gear_Main_Right',      axis: 'x', angleDeg: -90, phaseStart: 0.10, phaseEnd: 0.85 },
];

/**
 * Rolling wheels.
 *
 *  mesh       — the tyre mesh, parented to its gear leg (the nose wheel hangs
 *               off the steering collar)
 *  contact    — key into `aircraft.wheelGround` for this leg's ground state
 *  suspension — (optional) node that carries the oleo travel, if not the tyre
 *               itself. The nose wheel and its steering collar telescope as one
 *               piece; each main tyre slides up its own leg alone.
 *
 * Every wheel's local frame is aligned with its leg's, with +X along the axle:
 * the leg deploys by rotating ~-90° about local X, which swings local -Z down
 * the strut and local +Y out toward the nose. A positive X rotation therefore
 * walks the front of the tyre upward — rolling *backward* — which is why the
 * spin rate is negated below, and local +Z is "up the strut" for the suspension.
 * The left leg's mirrored scale flips its X axis but not the Y/Z plane the tyre
 * turns in, so both mains take the same spin angle and the same lift direction.
 */
export const WHEEL_CONFIG = [
  { mesh: '_Gear_Front_Wheel',      contact: 'nose',  suspension: '_Gear_Front_Steering' },
  { mesh: '_Gear_Main_Left_Wheel',  contact: 'left'  },
  { mesh: '_Gear_Main_Right_Wheel', contact: 'right' },
];

// Nose gear steering collar — the strut section the nose wheel is parented to.
// Its geometry runs down local -Y, so local Y is the (slightly raked) steering
// axis.
const STEERING_MESH = '_Gear_Front_Steering';

const _euler = new THREE.Euler();
const _targetQuat = new THREE.Quaternion();
const _deflectionQuat = new THREE.Quaternion();
const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _scale = new THREE.Vector3();
const _invQuat = new THREE.Quaternion();
const _wheelWorld = new THREE.Vector3();
const _arm = new THREE.Vector3();
const _contactVel = new THREE.Vector3();
const _lift = new THREE.Vector3();

const TWO_PI = Math.PI * 2;

const DEFAULT_SMOOTHING = 10;

/**
 * Tyre radius in world meters, measured off the mesh rather than configured.
 * The wheel geometry is centred on its node origin, so its extent normal to
 * the axle (local Y/Z) is the tyre diameter — scaled up by the model's world
 * scale, which the GLB bakes into the gear legs.
 * @param {THREE.Object3D} obj — the wheel mesh
 * @returns {number} radius in meters
 */
function measureWheelRadius(obj) {
  const geo = obj.geometry;
  if (!geo) return WHEELS.fallbackRadius;
  if (!geo.boundingBox) geo.computeBoundingBox();
  const bb = geo.boundingBox;
  const localRadius = Math.max(bb.max.y - bb.min.y, bb.max.z - bb.min.z) * 0.5;

  obj.updateWorldMatrix(true, false);
  obj.matrixWorld.decompose(_pos, _quat, _scale);
  // A mirrored leg decomposes to a negative axis — only magnitude matters here.
  const scale = Math.max(Math.abs(_scale.x), Math.abs(_scale.y), Math.abs(_scale.z));

  const radius = localRadius * scale;
  return radius > 0.01 ? radius : WHEELS.fallbackRadius;
}

export default class LandingGearAnimator {
  /**
   * @param {THREE.Object3D} modelRoot — the loaded GLB scene
   * @param {object[]}       config    — gear component descriptors
   * @param {number}         smoothing — slerp speed (higher = snappier)
   */
  constructor(modelRoot, config = GEAR_CONFIG, smoothing = DEFAULT_SMOOTHING) {
    this.components = [];
    this.smoothing = smoothing;

    for (const entry of config) {
      const obj = modelRoot.getObjectByName(entry.mesh);
      if (!obj) {
        console.warn(`LandingGearAnimator: mesh "${entry.mesh}" not found`);
        continue;
      }

      // Preserve the GLB rest-pose quaternion on first access so it survives
      // across scenario reloads (the same model object is reused).
      if (!obj.userData._originalQuat) {
        obj.userData._originalQuat = obj.quaternion.clone();
      }
      const baseQuat = obj.userData._originalQuat.clone();
      obj.quaternion.copy(baseQuat);

      this.components.push({
        obj,
        axis: entry.axis,
        angleRad: entry.angleDeg * DEG2RAD,
        phaseStart: entry.phaseStart,
        phaseEnd: entry.phaseEnd,
        phaseRange: entry.phaseEnd - entry.phaseStart,
        baseQuat,
        currentQuat: baseQuat.clone(),
      });
    }

    // ── Wheels ──────────────────────────────────────────────────────────────
    // Independent of the deploy sequence above: these are children of the legs
    // and carry only their own spin, so they're driven straight from physics
    // state rather than from the master gear value.
    this.wheels = [];
    this.noseWheel = null;
    for (const entry of WHEEL_CONFIG) {
      const obj = modelRoot.getObjectByName(entry.mesh);
      if (!obj) {
        console.warn(`LandingGearAnimator: wheel "${entry.mesh}" not found`);
        continue;
      }
      if (!obj.userData._originalQuat) {
        obj.userData._originalQuat = obj.quaternion.clone();
      }
      const baseQuat = obj.userData._originalQuat.clone();
      obj.quaternion.copy(baseQuat);

      // The node that takes the oleo stroke — the tyre itself unless the leg
      // telescopes as a larger assembly (the nose collar).
      let suspensionObj = obj;
      if (entry.suspension) {
        const found = modelRoot.getObjectByName(entry.suspension);
        if (found) suspensionObj = found;
        else console.warn(`LandingGearAnimator: suspension "${entry.suspension}" not found`);
      }
      if (!suspensionObj.userData._originalPos) {
        suspensionObj.userData._originalPos = suspensionObj.position.clone();
      }
      const restPos = suspensionObj.userData._originalPos.clone();
      suspensionObj.position.copy(restPos);

      const wheel = {
        obj,
        contact: entry.contact,
        baseQuat,
        radius: measureWheelRadius(obj),
        angle: 0, // accumulated roll (rad, wrapped)
        spin: 0,  // current roll rate (rad/s)
        suspension: suspensionObj,
        restPos,      // fully-extended local position of the suspension node
        compression: 0, // current stroke, in world meters up the strut
        // Starts planted so spawning cannot read as a touchdown: the ground
        // state is still all-false on the first frame (GroundContact hasn't run
        // yet), which would otherwise look like a landing.
        wasPlanted: true,
      };
      this.wheels.push(wheel);
      if (entry.contact === 'nose') this.noseWheel = wheel;
    }

    // ── Nose gear steering ──────────────────────────────────────────────────
    this.steering = null;
    const steerObj = modelRoot.getObjectByName(STEERING_MESH);
    if (steerObj) {
      if (!steerObj.userData._originalQuat) {
        steerObj.userData._originalQuat = steerObj.quaternion.clone();
      }
      const baseQuat = steerObj.userData._originalQuat.clone();
      steerObj.quaternion.copy(baseQuat);
      this.steering = { obj: steerObj, baseQuat, angle: 0 };
    } else {
      console.warn(`LandingGearAnimator: mesh "${STEERING_MESH}" not found`);
    }

    this.maxSteer = WHEELS.maxSteerDeg * DEG2RAD;
  }

  /**
   * Compute a component's fully-posed quaternion for a given master gear value.
   * @param {object}            c    — component descriptor
   * @param {number}            gear — master gear value (0 = retracted, 1 = deployed)
   * @param {THREE.Quaternion}  out  — quaternion to write the result into
   */
  _targetFor(c, gear, out) {
    // Map the master gear value to this component's local progress
    const localProgress = Math.max(0, Math.min(1,
      (gear - c.phaseStart) / c.phaseRange));

    const angleRad = localProgress * c.angleRad;

    _euler.set(
      c.axis === 'x' ? angleRad : 0,
      c.axis === 'y' ? angleRad : 0,
      c.axis === 'z' ? angleRad : 0,
    );

    _deflectionQuat.setFromEuler(_euler);
    return out.copy(c.baseQuat).multiply(_deflectionQuat);
  }

  /**
   * Call once per frame.
   * @param {Aircraft} aircraft — reads aircraft.gear (0 = retracted, 1 = deployed),
   *                              plus velocity / angularVelocity / wheelGround
   *                              for the wheels
   * @param {number}   dt       — frame delta in seconds
   */
  update(aircraft, dt) {
    const gear = aircraft.gear ?? 0;
    const t = 1 - Math.exp(-this.smoothing * dt);

    for (const c of this.components) {
      this._targetFor(c, gear, _targetQuat);
      c.currentQuat.slerp(_targetQuat, t);
      c.obj.quaternion.copy(c.currentQuat);
    }

    this._updateSteering(aircraft, dt);
    this._updateSuspension(aircraft, dt);
    this._updateWheels(aircraft, dt);
  }

  /**
   * Compress each oleo so its tyre rests on the surface instead of sinking into
   * it. The airframe is deliberately left free to tilt under flight loads —
   * rotating for takeoff, dipping under the brakes, leaning through a turn —
   * and the stroke absorbs whatever that tilt would have buried.
   *
   * Purely cosmetic: it reads the contact rays GroundContact already casts and
   * feeds nothing back, so the wheel moving cannot change the forces holding
   * the aircraft up.
   */
  _updateSuspension(aircraft, dt) {
    const rebound = 1 - Math.exp(-WHEELS.reboundRate * dt);

    for (const w of this.wheels) {
      const node = w.suspension;
      const parent = node.parent;
      if (!parent) continue;

      // Refreshing the tyre's world matrix refreshes every ancestor with it,
      // so the leg's matrix below is current too.
      w.obj.updateWorldMatrix(true, false);

      // Third column of the leg's world matrix: its direction is the strut axis
      // in world space, its length the scale a local offset picks up on the way
      // out. Reading it straight off the matrix also folds in the left leg's
      // mirrored scale, which would otherwise invert the stroke.
      const e = parent.matrixWorld.elements;
      _lift.set(e[8], e[9], e[10]);
      const legScale = _lift.length();
      if (legScale < 1e-6) continue; // degenerate leg transform — nothing to do
      _lift.divideScalar(legScale);

      let target = 0;
      // A leg that isn't pointing broadly downward — mid-retraction, or the
      // aircraft inverted — has no meaningful stroke, and its vanishing
      // vertical component would blow the travel up.
      if (_lift.y > 0.2) {
        _wheelWorld.setFromMatrixPosition(w.obj.matrixWorld);
        // Back out the lift already baked into that position to recover the
        // fully-extended height. Measuring against the *compressed* wheel would
        // close a loop — every lift would reduce the penetration that asked for
        // it — and the stroke would settle at a fraction of its true value.
        const restY = _wheelWorld.y - _lift.y * w.compression;

        // How deep the tyre would be buried at full extension. The stroke is
        // along the strut, so a tilted leg needs more of it per meter of depth.
        const buried = (aircraft.wheelGround[w.contact].groundY + w.radius) - restY;
        target = clamp(buried / _lift.y, 0, WHEELS.maxTravel);
      }

      // Compress instantly — the ground is not negotiable, and the physics
      // spring has already smoothed the penetration this tracks. Extension
      // lags, which is what reads as oleo rebound damping.
      w.compression = target > w.compression
        ? target
        : w.compression + (target - w.compression) * rebound;

      node.position.copy(w.restPos);
      node.position.z += w.compression / legScale;
    }
  }

  /**
   * Roll each tyre. A planted wheel is dragged toward the no-slip rolling rate
   * by contact-patch friction (fast, but not instant — that lag is the
   * touchdown spin-up); a wheel in the air coasts down under bearing and
   * windage drag until it stops.
   *
   * Contact state is whatever GroundContact published last frame — the gear
   * animation has to run first so GroundContact can raycast from the posed
   * struts, so a one-frame lag here is inherent and invisible.
   */
  _updateWheels(aircraft, dt) {
    // Speed along the nose — what a free-rolling tyre tracks. forwardVector is
    // a shared cached vector, so only read from it.
    const vLongitudinal = aircraft.velocity.dot(aircraft.forwardVector);
    const spinUp = 1 - Math.exp(-WHEELS.spinUpRate * dt);
    const drag = Math.exp(-dt / WHEELS.spinDownTau);
    const bearing = WHEELS.spinDownDecel * dt;

    for (const w of this.wheels) {
      const planted = aircraft.wheelGround[w.contact].onGround;

      // Touchdown puff, on the frame the tyre first meets the ground. Gated on
      // how fast the contact patch is being scrubbed — the surface speed of the
      // tyre against the surface speed of the runway — because that scrub is
      // what actually throws the dust up. A wheel that skips a bump mid-rollout
      // comes back down already turning at ground speed and stays clean.
      if (planted && !w.wasPlanted) {
        const slip = Math.abs(vLongitudinal + w.spin * w.radius);
        if (slip > WHEELS.touchdownSlip) this._spawnTouchdownDust(aircraft, w);
      }
      w.wasPlanted = planted;

      if (planted) {
        // Rolling without slip, negated because +X rolls the tyre backward.
        const target = -vLongitudinal / w.radius;
        w.spin += (target - w.spin) * spinUp;
      } else {
        w.spin *= drag;
        // Constant bearing friction on top of the exponential, so the wheel
        // reaches a definite stop instead of creeping toward one forever.
        w.spin = Math.abs(w.spin) <= bearing
          ? 0
          : w.spin - Math.sign(w.spin) * bearing;
      }

      w.angle = (w.angle + w.spin * dt) % TWO_PI;
      _euler.set(w.angle, 0, 0);
      _deflectionQuat.setFromEuler(_euler);
      w.obj.quaternion.copy(w.baseQuat).multiply(_deflectionQuat);
    }
  }

  /**
   * Kick a puff of dust out from under a tyre that has just touched down.
   * Dropped at the contact patch and thrown backward along the aircraft's
   * track, the same way the belly-slide dust in Aircraft trails.
   */
  _spawnTouchdownDust(aircraft, w) {
    const particles = aircraft.particleSystem;
    if (!particles) return;

    // _updateSuspension already refreshed this matrix earlier in the frame.
    _wheelWorld.setFromMatrixPosition(w.obj.matrixWorld);
    const v = aircraft.velocity;
    const trail = WHEELS.touchdownDrag;

    particles._emit('dustSmall',
      _wheelWorld.x,
      aircraft.wheelGround[w.contact].groundY,
      _wheelWorld.z,
      -v.x * trail + srand() * 1.5,
      rand(1, 3),
      -v.z * trail + srand() * 1.5,
    );
  }

  /**
   * Swivel the nose gear while there's weight on it, and let it drift back to
   * centre once it lifts.
   *
   * The wheel is aimed along the track its own contact patch is cutting across
   * the ground — never along the commanded heading change. A rolling wheel can
   * only point where it is actually going, and the two diverge badly whenever
   * the airframe's yaw lags the rudder (the ground model applies a yaw torque
   * directly, so there is no wheel angle implied by the command at all).
   */
  _updateSteering(aircraft, dt) {
    const s = this.steering;
    if (!s) return;

    let target = 0;
    let rate = WHEELS.centerRate; // unloaded: the wheel eases back to centre

    if (aircraft.wheelGround.nose.onGround) {
      this._noseTrackVelocity(aircraft, _contactVel);

      // Body axes: +X right, -Z forward. Pointing the wheel along (vx, vz)
      // means sin = -vx/speed, cos = -vz/speed. Rolling backward traces the
      // same line as rolling forward, so fold the direction into the forward
      // half-plane first rather than snapping the wheel through 180°.
      let fx = -_contactVel.x;
      let fz = -_contactVel.z;
      if (fz < 0) { fx = -fx; fz = -fz; }
      // Clamped to the mechanical stop: a patch sliding harder sideways than
      // the stop allows really is skidding, and should look like it.
      target = clamp(Math.atan2(fx, fz), -this.maxSteer, this.maxSteer);

      // That atan2 degenerates into noise as the patch slows to a stop, so
      // fade the slew rate out with speed — at zero the wheel simply holds
      // whatever angle it stopped at, exactly as a parked one does.
      const speed = Math.hypot(_contactVel.x, _contactVel.z);
      rate = WHEELS.steerRate * smoothstep(
        (speed - WHEELS.trackMinSpeed) / (WHEELS.trackFullSpeed - WHEELS.trackMinSpeed));
    }

    s.angle += (target - s.angle) * (1 - Math.exp(-rate * dt));

    // The collar's local +Y points up (its -Y runs down the strut), so a
    // positive angle steers the wheel left — matching the body-axis maths above.
    _euler.set(0, s.angle, 0);
    _deflectionQuat.setFromEuler(_euler);
    s.obj.quaternion.copy(s.baseQuat).multiply(_deflectionQuat);
  }

  /**
   * Ground velocity of the nose wheel's contact patch, in body axes.
   *
   * Taken from the wheel's own world position rather than a fixed arm so it
   * follows the leg wherever the deploy animation has it — v = v_cg + ω × r.
   * The wheel node sits within a centimetre of the steering axis, so reading it
   * after last frame's swivel feeds nothing meaningful back into the angle.
   */
  _noseTrackVelocity(aircraft, out) {
    const obj = this.noseWheel ? this.noseWheel.obj : this.steering.obj;
    obj.updateWorldMatrix(true, false);
    _wheelWorld.setFromMatrixPosition(obj.matrixWorld);
    _arm.subVectors(_wheelWorld, aircraft.position);

    out.copy(aircraft.angularVelocity).cross(_arm).add(aircraft.velocity);
    _invQuat.copy(aircraft.quaternion).invert();
    out.applyQuaternion(_invQuat);
  }

  /**
   * Jump every component straight to the pose for the aircraft's current gear
   * value, skipping the slerp. Used on scenario spawn so a gear-down start
   * doesn't visibly animate open from the retracted rest pose.
   * @param {Aircraft} aircraft — reads aircraft.gear (0 = retracted, 1 = deployed)
   */
  snap(aircraft) {
    const gear = aircraft.gear ?? 0;
    for (const c of this.components) {
      this._targetFor(c, gear, c.currentQuat);
      c.obj.quaternion.copy(c.currentQuat);
    }

    // Wheels spawn stopped, straight and fully extended — the model object is
    // shared across scenario reloads, so the poses have to be reset, not just
    // the state. The suspension settles on its own within a frame or two.
    for (const w of this.wheels) {
      w.angle = 0;
      w.spin = 0;
      w.compression = 0;
      w.wasPlanted = true; // no touchdown puff on the spawn frame
      w.obj.quaternion.copy(w.baseQuat);
      w.suspension.position.copy(w.restPos);
    }
    if (this.steering) {
      this.steering.angle = 0;
      this.steering.obj.quaternion.copy(this.steering.baseQuat);
    }
  }
}
